import { useState } from "react";

import { isNil } from "lodash-es";
import moment from "moment";
import { ShiftNavigatorDirection } from "../component/shift-navigator.control";
import { ShiftInfo } from "../models/shift-navigator.model";
import { getShiftByParameters } from "../repositories/ShiftsRepository";

export const useShiftNavigatorManager = () => {
	const [shiftInfo, setShiftInfo] = useState<ShiftInfo | null>(null);
	const [isShiftInfoLoading, setIsShiftInfoLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");

	const moveShift = async (
		direction: ShiftNavigatorDirection,
		assetId?: number | null,
	): Promise<ShiftInfo | void> => {
		if (isNil(shiftInfo)) {
			return;
		}
		setIsShiftInfoLoading(true);

		let shiftId: number | null = null;
		if (direction === "Next" && shiftInfo.NextShiftId !== null) {
			shiftId = shiftInfo.NextShiftId;
		} else if (direction === "Previous" && shiftInfo.PreviousShiftId !== null) {
			shiftId = shiftInfo.PreviousShiftId;
		} else if (direction === "Last" && shiftInfo.LastShiftId !== null) {
			shiftId = shiftInfo.LastShiftId;
		}

		if (shiftId) {
			const resp = await getShiftByParameters({
				productionDate: null,
				shiftId: shiftId,
				assetId,
			});
			if (resp.ok) {
				const shift: ShiftInfo = {
					...resp.data,
					CurrentProductionDate: moment
						.utc(resp.data.CurrentProductionDate)
						.toDate(),
					CurrentStart: moment.utc(resp.data.CurrentStart).toDate(),
					CurrentEnd: moment.utc(resp.data.CurrentEnd).toDate(),
					PreviousProductionDate: moment
						.utc(resp.data.PreviousProductionDate)
						.toDate(),
					PreviousStart: moment.utc(resp.data.PreviousStart).toDate(),
					PreviousEnd: moment.utc(resp.data.PreviousEnd).toDate(),
					LastProductionDate: moment.utc(resp.data.LastProductionDate).toDate(),
					LastStart: moment.utc(resp.data.LastStart).toDate(),
					LastEnd: moment.utc(resp.data.LastEnd).toDate(),
				};
				setShiftInfo(shift);
				return shift;
			}
		}
	};

	const getShiftDataFromAPI = async (
		productionDate: Date | null,
		shiftId: number,
		assetId?: number | null,
	): Promise<ShiftInfo | void> => {
		setIsShiftInfoLoading(true);
		try {
			const resp = await getShiftByParameters({
				productionDate: productionDate,
				shiftId: shiftId,
				assetId,
			});
			if (resp.ok) {
				const shift: ShiftInfo = {
					...resp.data,
					CurrentProductionDate: moment
						.utc(resp.data.CurrentProductionDate)
						.toDate(),
					CurrentStart: moment.utc(resp.data.CurrentStart).toDate(),
					CurrentEnd: moment.utc(resp.data.CurrentEnd).toDate(),
					PreviousProductionDate: moment
						.utc(resp.data.PreviousProductionDate)
						.toDate(),
					PreviousStart: moment.utc(resp.data.PreviousStart).toDate(),
					PreviousEnd: moment.utc(resp.data.PreviousEnd).toDate(),
					LastProductionDate: moment.utc(resp.data.LastProductionDate).toDate(),
					LastStart: moment.utc(resp.data.LastStart).toDate(),
					LastEnd: moment.utc(resp.data.LastEnd).toDate(),
				};
				setShiftInfo(shift);
				return shift;
			}
		} catch (e: any) {
			setError(e.toString());
		}
		setIsShiftInfoLoading(false);
	};

	const initShiftNavigator = async (
		assetId?: number | null,
	): Promise<ShiftInfo | void> => {
		const resp = await getShiftByParameters({
			productionDate: new Date(),
			shiftId: 0,
			assetId,
		});
		if (resp.ok) {
			const shift: ShiftInfo = {
				...resp.data,
				CurrentProductionDate: moment
					.utc(resp.data.CurrentProductionDate)
					.toDate(),
				CurrentStart: moment.utc(resp.data.CurrentStart).toDate(),
				CurrentEnd: moment.utc(resp.data.CurrentEnd).toDate(),
				PreviousProductionDate: moment
					.utc(resp.data.PreviousProductionDate)
					.toDate(),
				PreviousStart: moment.utc(resp.data.PreviousStart).toDate(),
				PreviousEnd: moment.utc(resp.data.PreviousEnd).toDate(),
				LastProductionDate: moment.utc(resp.data.LastProductionDate).toDate(),
				LastStart: moment.utc(resp.data.LastStart).toDate(),
				LastEnd: moment.utc(resp.data.LastEnd).toDate(),
			};
			setShiftInfo(shift);
			return shift;
		}
	};

	return {
		shiftInfo,
		moveShift,
		getShiftDataFromAPI,
		initShiftNavigator,
	};
};

export type ShiftNavigatorManagerResult = {
	shiftInfo: ShiftInfo | null;
	moveShift: (
		direction: ShiftNavigatorDirection,
		assetId?: number | null,
	) => Promise<ShiftInfo | void>;
	getShiftDataFromAPI: (
		productionDate: Date | null,
		shiftId: number,
		assetId?: number | null,
	) => Promise<ShiftInfo | void>;
	initShiftNavigator: (assetId?: number | null) => Promise<ShiftInfo | void>;
};
